import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  Add,
  PublishedWithChanges
} from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ConfirmationDialog from '../Dialog/ConfirmationDialog';
import CustomButton from '../Common/CustomButton';
import TestCaseSet from './TestCaseSet';
import LoadingOverlay from '../Common/LoadingOverlay';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { updateTestCaseSetList, setExpandedTestCaseSetId } from "../../redux-store/testCaseSetReducers/testCaseSetSlice";
import { fetchTestCaseSetList, deleteTestCaseSet } from '../../redux-store/testCaseSetReducers/testCaseSetActions';
import { updateTestScenario, fetchTestScenarioCode, updateTestScenarioYMLCode } from '../../redux-store/testScenarioReducers/testScenarioActions';
import { getAllVariableSets } from '../../redux-store/variableSetReducers/variableSetActions';
import { LOCAL_STORAGE_KEY_FOR_ENVIRONMENT } from 'config/constants';
import { addTestScenarioRequest } from 'redux-store/aiChatsReducers/aiChatsActions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CodeViewer from '../Common/CodeViewer';
import { Download } from 'lucide-react';
import JSZip from 'jszip';
import AgentSelectionDialog from '../Common/AgentSelectionDialog';
import { Settings } from 'lucide-react';

const TestCaseSetList = function ({ testScenarioId, getNewTestCase, onDelete, project, isAiAgent = false, createTestScenarioRequestAIAgent, expandTestCaseIdOnLoad = null }) {
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const [expandedId, setExpandedId] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTestCaseSetDetail, setDeleteTestCaseSetDetail] = useState("");
  const testCaseSetList = useSelector(state => (state.testCaseSet.testCaseSetList));
  const isLoading = useSelector(state => state.testCaseSet.isLoading);
  const [showValidateConfirmation, setShowValidateConfirmation] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
  const [codeFiles, setCodeFiles] = useState([]);
  const [visibleCodeFiles, setVisibleCodeFiles] = useState([]);
  const [view, setView] = useState("preview");
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const selectedTestScenarioDetail = useSelector(
    (state) => state.testScenario.selectedTestScenarioDetail
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('cloudAgent');
  const [extensionInstalled, setExtensionInstalled] = useState(false);

  const getAllTestCases = async () => {
    try {
      setIsDataLoaded(false);
      const response = await dispatch(fetchTestCaseSetList({ testScenarioId: testScenarioId }));
      if (response?.payload?.length === 0) {
        await dispatch(updateTestCaseSetList({ testCaseSetList: [getNewTestCase()] }));
      }
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Error fetching test cases:', error);
      openSnackbar({
        message: "Failed to fetch test cases. Please try again.",
        severity: "error",
      });
      setIsDataLoaded(true);
    }
  };

  useEffect(() => {
    getAllTestCases();
    fetchEnvironments();
    // Check extension installed and agent selection
    const check = async () => {
      if (!localStorage.getItem('runOn')) {
        localStorage.setItem('runOn', 'cloudAgent');
      }
      const { checkExtensionInstalled } = await import('../../utils/extensionUtils');
      const installed = await checkExtensionInstalled();
      if (!installed) {
        localStorage.setItem('runOn', 'cloudAgent');
      }
      setSelectedAgent(localStorage.getItem('runOn'));
      setExtensionInstalled(installed);
    };
    check();
  }, [project.id, testScenarioId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        setContainerWidth(newWidth);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const fetchEnvironments = async () => {
    try {
      if (project.id) {
        const result = await dispatch(getAllVariableSets({
          projectId: project.id
        })).unwrap();
        if (result?.variable_sets) {
          setEnvironments(result.variable_sets);
          const savedEnvironmentId = localStorage.getItem(LOCAL_STORAGE_KEY_FOR_ENVIRONMENT) || project.primary_environment_id;

          if (savedEnvironmentId && result.variable_sets.length > 0) {
            // Find environment with saved ID
            const savedEnvironment = result.variable_sets.find(env => env.id === savedEnvironmentId);
            if (savedEnvironment) {
              setSelectedEnvironmentId(savedEnvironment.id);
              return;
            }
          }

          // Fallback: use default environment or first available
          if (result.variable_sets.length > 0) {
            const defaultEnv = result.variable_sets.find(env => env.title === 'default') || result.variable_sets[0];
            setSelectedEnvironmentId(defaultEnv.id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      openSnackbar({
        message: "Failed to fetch environments. Please try again.",
        severity: "error",
      });
    }
  };

  const handleEnvironmentChange = (event) => {
    localStorage.setItem(LOCAL_STORAGE_KEY_FOR_ENVIRONMENT, event.target.value);
    setSelectedEnvironmentId(event.target.value);
  };

  const onAddNewTestCase = async () => {
    await dispatch(updateTestCaseSetList({ testCaseSetList: [...testCaseSetList, getNewTestCase()] }));
  };

  const updateOrder = async (testCaseSetListUpdated) => {
    await dispatch(updateTestScenario({
      testScenarioId: testScenarioId,
      data: {
        test_case_sets_order: testCaseSetListUpdated.filter(item => !item.isNew).map(item => item.id)
      }
    }));
  };

  const updateList = async (testCaseSetListUpdated, orderChanged = false) => {
    await dispatch(updateTestCaseSetList({ testCaseSetList: [...testCaseSetListUpdated] }));
    if (orderChanged) {
      updateOrder(testCaseSetListUpdated);
    }
  };

  const onExpand = (id) => {
    if (id !== expandedId) {
      setExpandedId(id);
      dispatch(setExpandedTestCaseSetId(id));
    } else {
      setExpandedId("");
      dispatch(setExpandedTestCaseSetId(null));
    }
  };

  useEffect(() => {
    if (expandTestCaseIdOnLoad) {
      onExpand(expandTestCaseIdOnLoad);
      setTimeout(() => {
        document
          .getElementById(expandTestCaseIdOnLoad)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    }
  }, []);

  const onClickDelete = (testCaseSet) => {
    setDeleteTestCaseSetDetail(testCaseSet);
    onToggleDeleteDialog();
  };

  const onDeleteConfirm = async () => {
    const testCaseSetIndex = testCaseSetList.findIndex(testCaseSet => testCaseSet.id === deleteTestCaseSetDetail.id);
    const testCaseSetListUpdated = [
      ...testCaseSetList.slice(0, testCaseSetIndex),
      ...testCaseSetList.slice(testCaseSetIndex + 1, testCaseSetList.length)
    ];

    if (!deleteTestCaseSetDetail.isNew) {
      await dispatch(deleteTestCaseSet({ testCaseSetId: deleteTestCaseSetDetail.id }));
    }
    await dispatch(updateTestCaseSetList({ testCaseSetList: [...testCaseSetListUpdated] }));
    onToggleDeleteDialog();
    updateOrder(testCaseSetListUpdated);
    openSnackbar({ message: "Step deleted successfully.", severity: "success" });
    if (testCaseSetListUpdated.length === 0) {
      getAllTestCases();
    }
  };

  const onToggleDeleteDialog = () => {
    setDeleteDialogOpen(prevState => !prevState);
    if (deleteTestCaseSetDetail?.id) {
      setDeleteTestCaseSetDetail({});
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }
    const draggedTestCase = testCaseSetList[result?.source?.index];
    const sourceIndex = result?.source?.index;
    const destinationIndex = result?.destination?.index;

    if (sourceIndex > destinationIndex) {
      const firstHalf = testCaseSetList.slice(0, result?.destination?.index);
      const secondHalf = testCaseSetList.slice(result?.destination?.index).filter(item => item.id !== result?.draggableId);
      updateList([...firstHalf, draggedTestCase, ...secondHalf], true);
    } else {
      const firstHalf = testCaseSetList.slice(0, result?.destination?.index + 1).filter(item => item.id !== result?.draggableId);
      const secondHalf = testCaseSetList.slice(result?.destination?.index + 1);
      updateList([...firstHalf, draggedTestCase, ...secondHalf], true);
    }
  };

  const renderConfirmDialog = () => {
    return (
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={onToggleDeleteDialog}
        onConfirm={onDeleteConfirm}
        title="Confirm Delete"
        content="Are you sure you want to delete?"
      />
    );
  };
  useEffect(() => {
    getSelectedTestScenarioCode();
  }, [view, selectedEnvironmentId])

  const handleView = (event, selectedView) => {
    setView(selectedView);
  }

  const getSelectedTestScenarioCode = async () => {
    if (selectedEnvironmentId === "") return;
    const response = await dispatch(fetchTestScenarioCode({ testScenarioId: testScenarioId, environmentSetId: selectedEnvironmentId })).unwrap();
    if (response) {
      // Set all files
      setCodeFiles(response.code_files);
      // Filter and set visible files
      const visibleFiles = response.code_files.filter(file => file.visible !== false);
      setVisibleCodeFiles(visibleFiles);
    }
  }

  const generateRequestDataInAiAgent = async (testScenarioId) => {
    try {
      const response = await dispatch(
        addTestScenarioRequest({ aiChatId: null, testScenarioId: testScenarioId })
      )
      if (response.payload.status === 200) {
        openSnackbar({ message: "Request creation initiated for Test Scenario. Redirecting to AI Agent for updates.", severity: 'info' });
        setTimeout(() => {
          window.open(`/ai-agent?chatId=${response.payload.data.id}`, "_blank");
        }, 500)
      }
    } catch (error) {
      openSnackbar({ message: "Error creating Test Scenario Request", severity: 'error' });
    }
  };

  const renderValidateConfirmDialog = () => {
    return (
      <ConfirmationDialog
        open={showValidateConfirmation}
        onClose={() => setShowValidateConfirmation(false)}
        onConfirm={() => {
          setShowValidateConfirmation(false);
          generateRequestDataInAiAgent(testScenarioId)
        }}
        title="Confirm Action"
        content="This will delete all the request body and headers and will setup everything again with AI."
        actionText="Continue"
        cancelText="Cancel"
        actionTextColor="primary"
        cancelTextColor="error"
      />
    );
  };

  const renderDragNDrop = () => {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {testCaseSetList.map((testCaseSet, index) => (
                <Draggable
                  key={testCaseSet.id}
                  draggableId={testCaseSet.id}
                  index={index}
                  isDragDisabled={expandedId === testCaseSet.id}
                >
                  {(provided, snapshot) => (
                    <div className='bg-[var(--background-primary)]'>
                      <div
                        id={testCaseSet.id}
                        ref={provided.innerRef}
                        {...provided.dragHandleProps}
                        {...provided.draggableProps}
                      >
                        <Box sx={{ width: "100%", marginBottom: "16px" }} key={testCaseSet.id}>
                          <TestCaseSet
                            key={testCaseSet.id}
                            detail={testCaseSet}
                            isExpanded={expandedId === testCaseSet.id}
                            updateList={updateList}
                            onExpand={onExpand}
                            onClickDelete={() => onClickDelete(testCaseSet)}
                            selectedEnvironmentId={selectedEnvironmentId}
                          />
                        </Box>
                      </div>
                      {provided.placeholder}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  const onValidateBtnClick = () => {
    setShowValidateConfirmation(true);
  };

  const handleMarkAsActive = async () => {
    try {
      await dispatch(updateTestScenario({
        testScenarioId: testScenarioId,
        data: {
          status: 'active'
        }
      }));
      openSnackbar({
        message: "Test scenario successfully marked as active.",
        severity: "success",
      });
    } catch (error) {
      console.error('Error updating test scenario status:', error);
      openSnackbar({
        message: "Failed to mark test scenario as active. Please try again.",
        severity: "error",
      });
    }
  };

  const downloadCodeFiles = async () => {
    try {
      const folderName = `TS-${testScenarioId}`;
      const zip = new JSZip();
      const mainFolder = zip.folder(folderName);
      codeFiles.forEach(file => {
        const content = typeof file.file_content === 'string'
          ? file.file_content
          : JSON.stringify(file.file_content, null, 2);
        const options = {
          binary: file.file_name.endsWith('.sh'), // Treat .sh files as binary
          unixPermissions: file.file_name.endsWith('.sh') ? 0o755 : 0o644 // Set executable permissions for .sh files
        };
        mainFolder.file(file.file_name, content, options);
      });

      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 9
        }
      });

      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folderName}.zip`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading files:', error);
      openSnackbar({
        message: "Failed to download code files. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSaveCodeFiles = async (editedFile) => {
    try {
      const response = await dispatch(updateTestScenarioYMLCode({
        testScenarioId: testScenarioId,
        code: editedFile.file_content
      })).unwrap();
      if (response) {
        getSelectedTestScenarioCode();
        openSnackbar({
          message: "File saved successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error('Error saving file:', error);
      openSnackbar({
        message: "Failed to save file. Please try again.",
        severity: "error",
      });
    }
  };

  const handleAgentDialogOpen = (e) => {
    e.preventDefault();
    setAgentDialogOpen(true);
  };
  const handleAgentDialogSave = (agentValue) => {
    setSelectedAgent(agentValue);
    setAgentDialogOpen(false);
    localStorage.setItem('runOn', agentValue);
  };
  const handleAgentDialogClose = () => {
    setAgentDialogOpen(false);
  };

  let content = null;

  if ((isLoading || !isDataLoaded) && (!testCaseSetList || testCaseSetList.length === 0)) {
    content = (
      <Box sx={{ width: "100%", textAlign: "center", padding: "50px 32px" }}>
        <h3>Loading test cases...</h3>
      </Box>
    );
  }

  if (testCaseSetList?.length > 0 && isDataLoaded) {
    if (view === "preview") {
      content = (
        <div style={{ paddingTop: "16px", paddingBottom: "150px" }}>
          {renderDragNDrop()}
          <CustomButton startIcon={<Add />} onClick={onAddNewTestCase}>
            Add New Step
          </CustomButton>
        </div>
      );
    } else {
      content = (
        <Box sx={{ mt: 2 }}>
          <CodeViewer files={visibleCodeFiles} onSave={handleSaveCodeFiles} />
        </Box>
      );
    }
  }

  return (
    <Box ref={containerRef}>
      <Box sx={{ display: 'flex', justifyContent: containerWidth > 725 ? 'space-between' : 'flex-start', alignItems: 'center', mb: 2, flexDirection: containerWidth > 725 ? 'row' : 'column' }}>
        <Box className={`flex items-center gap-2 ${containerWidth > 725 ? '' : 'w-full'}`}>
          <FormControl size="small">
            <InputLabel id="environment-select-label">Environment</InputLabel>
            <Select
              labelId="environment-select-label"
              id="environment-select"
              value={selectedEnvironmentId}
              label="Environment"
              onChange={handleEnvironmentChange}
              sx={{ minWidth: "100%", maxWidth: "120px" }}
            >
              {environments.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <button
            onClick={handleAgentDialogOpen}
            className='bg-transparent border !border-[var(--borderColor)] !rounded-md py-1 text-[var(--text-primary)] flex items-center gap-1 text-xs'
            style={{ height: '36px', minWidth: '120px', padding: '0 12px' }}
          >
            <span><Settings size={16} /></span>
            <span className='text-sm'> {selectedAgent === 'cloudAgent' ? 'Cloud Agent' : 'Local Agent'}</span>
          </button>
          <div className='flex items-center gap-2'>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleView}
              aria-label="View"
              className='my-2'
              sx={{
                borderRadius: '4px',
                backgroundColor: 'var(--background-layout)',
                padding: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '3px !important',
                  margin: '0',
                  color: 'var(--text-primary)',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--background-primary)',
                    color: "var(--primary-color)",
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: 'var(--background-primary)'
                    }
                  },
                  '&:not(.Mui-selected)': {
                    '&:hover': {
                      backgroundColor: 'var(--background-layout)'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="preview" aria-label="Preview" className='px-3 py-1'>
                Preview
              </ToggleButton>
              <ToggleButton value="code" aria-label="Code" className='px-3 py-1'>
                Code
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              size="small"
              className='my-2'
              onClick={downloadCodeFiles}
              sx={{
                borderRadius: '4px',
                backgroundColor: 'var(--background-layout)',
                padding: '4px',
                zIndex: 1,
                color: "var(--text-secondary)",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36.44px',
                height: '36.44px',
                minWidth: '32px',
                border: "none",
                boxShadow: "none",
                '&:hover': {
                  backgroundColor: 'var(--hover-bg)',
                  border: "none",
                }
              }}
            >
              <Download size={18} />
            </Button>
          </div>
          <AgentSelectionDialog
            open={agentDialogOpen}
            onClose={handleAgentDialogClose}
            onSave={handleAgentDialogSave}
            selectedAgent={selectedAgent}
            extensionInstalled={extensionInstalled}
          />
        </Box>
        <Box className={`flex items-center gap-2 ${containerWidth > 725 ? '' : 'w-full justify-start'}`}>
          {selectedTestScenarioDetail?.status === 'draft' && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleMarkAsActive}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                height: '36px',
                borderRadius: '4px',
                borderColor: 'var(--primary-color)',
                color: 'var(--primary-color)',
                '&:hover': {
                  borderColor: 'var(--primary-color-light)',
                  backgroundColor: 'var(--hover-bg)'
                },
                minWidth: "150px"
              }}
              startIcon={<PublishedWithChanges fontSize="small" />}
            >
              Mark active
            </Button>
          )}
          {
            !isAiAgent && (
              (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onValidateBtnClick}
                  sx={{
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px',
                    height: '36px',
                    backgroundColor: 'var(--primary-color)',
                    '&:hover': {
                      backgroundColor: 'var(--primary-color-light)'
                    },
                    minWidth: "170px"
                  }}
                  startIcon={<AutoAwesomeIcon fontSize="small" sx={{ marginRight: '0px' }} />}
                >
                  Generate Payload
                </Button>
              )
            )
          }
          {isAiAgent && createTestScenarioRequestAIAgent && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => createTestScenarioRequestAIAgent(testScenarioId)}
              sx={{
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                height: '36px',
                backgroundColor: 'var(--primary-color)',
                '&:hover': {
                  backgroundColor: 'var(--primary-color-light)'
                },
                minWidth: "170px"
              }}
              startIcon={<AutoAwesomeIcon fontSize="small" sx={{ marginRight: '0px' }} />}
            >
              Generate Payload
            </Button>
          )}
        </Box>
      </Box>
      <span>Steps</span>
      {content}
      {deleteDialogOpen && renderConfirmDialog()}
      {showValidateConfirmation && renderValidateConfirmDialog()}
      <LoadingOverlay isLoading={isLoading} />
    </Box>
  );
}

export default TestCaseSetList;